import angular, {
    IComponentController,
    IOnChangesObject,
    IPromise,
    IQService,
    IScope,
    ITimeoutService,
} from 'angular';
import {
    CharacterSet,
    CodePageCaseSupport,
    FontFamilyService,
    FontStyleService,
    IFontStyleModel,
    VariationSettings,
    getCssFontVariationSettings,
    getFontName,
    hasOwnProperty,
    opentypeFeatureCSS,
} from '../../tn.common';
import {
    OPENTYPE_FEATURES,
    OPENTYPE_FEATURE_GROUPS,
} from '../../tn.common/constants';
import { isEmoji } from '../../tn.common/functions/isEmoji';
import DjangoClient from '../../tn.django/django-client.service/djangoClient';
import { IPopover, PopoverService } from '../../tn.popover/service.tn.popover';
import { FontLoader } from '../fontloader.service/fontloader.service';
import tnGlyphsTemplate from './tn-glyphs.html';

interface UnicodeCharacter {
    name: string;
    char: string;
    codepoint: string;
}

interface UnicodeCasePair {
    name: string;
    lower: UnicodeCharacter;
    upper: UnicodeCharacter;
}

/**
 * @ngdoc component
 * @name  tn-glyphs
 * @param {IFontStyleModel} targetStyle The Style that is currently being evaluated.
 * @param {VariationSettings} varationSettings override the default variation settings of the target style.
 *
 * @description
 * A public directive that can be used to show a variety of information on
 * a style's individual glpyhs. Will emit with an information block on the contents
 * of the style, as well as expando trees representing the entire glyph set of a style
 *
 * @example
 * <tn-glyphs target-style="vm.style"></tn-glyphs>
 */

const figures = `Different kinds of text call for different kinds of figures. Proportional figures work well in
 running text; lining figures (usually the same height as caps) are designed to work well with strings of
 capitals; tabular figures are of uniform width and perform best in charts and tables, or anywhere numerals
 need to be aligned vertically. Which figures—proportional, lining, or tabular—are set as the default varies
 from typeface to typeface, and not all typefaces offer alternate figures. Make sure the fonts you choose
 have the kind of figures you need.`;
const otherNumerics = `Some families feature special numerics like a slashed zero (to avoid confusion
 with o and O), ordinals, fractions, numerators, denominators, and scientific inferiors.`;
const punctuation = 'Punctuation.';
const otherFeatures = `Some typefaces offer specialized features like localized forms, subscripts,
 superscripts, and ornaments. If your project calls for them, make sure to choose a typeface that offers
 them. When in doubt, check the glyph set.`;
const smallCaps = `Small caps, available via the OpenType “Small Capitals” feature, are carefully engineered
 smaller capital letters (usually slightly taller than the x-height) meant to blend in well with running
 text. The "Small Capitals From Capitals" feature can be useful for changing strings of capital letters
 (such as acronyms) to small caps. Not all typefaces have small caps.`;
const replacements = `To help text flow optimally, or purely for aesthetic reasons, some typefaces offer
 enhancements in the form of standard and discretionary ligatures, swashes, contextual alternates, and
 stylistic alternates. Not all typefaces have—or require—these replacements.`;

const featureGroupTitles = [
    'Figures',
    'Other Numerics',
    'Punctuation',
    'Other Features',
    'Small Caps',
    'Replacements',
] as const;

const featureGroupInstructions = {
    Figures: figures,
    'Other Numerics': otherNumerics,
    Punctuation: punctuation,
    'Other Features': otherFeatures,
    'Small Caps': smallCaps,
    Replacements: replacements,
} as const;

export class TnGlyphsComponentBindings {
    targetStyle?: IFontStyleModel;
    variationSettings?: VariationSettings;
}

export class TnGlyphsComponent
    extends TnGlyphsComponentBindings
    implements IComponentController
{
    character_set?: CharacterSet;
    activeGlyph: unknown = null;
    activeLanguage = 'Latin';

    featureGroupInstructions = featureGroupInstructions;
    featureGroupTitles = featureGroupTitles;

    glyphSpecimenPopover?: IPopover;
    codepageSupport?: Record<string, unknown>;

    static $inject = [
        '$scope',
        '$q',
        '$tnPopover',
        'djangoClient',
        '$timeout',
        'FontFamilyService',
        'FontStyleService',
        'tn.fontloader.service',
    ];
    fontName = '';
    fontVariations = '';
    featureGroupCount = 0;
    featureGroups: Record<string, { code: string; name: string }> = {};

    glyphCss = '';
    fontCss = '';

    constructor(
        protected $scope: IScope,
        protected $q: IQService,
        protected $tnPopover: PopoverService,
        protected djangoClient: DjangoClient,
        protected $timeout: ITimeoutService,
        protected familyService: FontFamilyService,
        protected styleService: FontStyleService,
        protected fontLoader: FontLoader
    ) {
        super();
    }

    updateFontCss(): void {
        const fontName =
            (this.targetStyle && getFontName(this.targetStyle)) || '';
        const fontVariationCss = getCssFontVariationSettings(
            this.variationSettings || this.targetStyle.axis?.axes || {}
        );
        this.fontCss = `font-family: "${fontName}", "Adobe Blank"; font-variation-settings: ${fontVariationCss};`;
    }

    $onChanges(changes: IOnChangesObject): void {
        // console.log('TnGlyphsController.$onChanges', changes);
        if (changes.variationSettings) {
            this.updateFontCss();
        }
        if (changes.targetStyle) {
            if (this.targetStyle) {
                this.updateFontCss();
                this.codepageSupport = this.filterCodepageThreshold(
                    this.targetStyle.support.codepageSupport
                );
                // wait for the full charset load then hit it
                this.fetchStyleCharset(this.targetStyle).then((charSet) => {
                    this.character_set = charSet;
                    // console.log('charSet', charSet, this.targetStyle);
                    if (!this.targetStyle) {
                        this.featureGroups = {};
                        this.featureGroupCount = 0;
                    } else {
                        this.featureGroups = this.getFeatureGroups(
                            this.targetStyle,
                            charSet
                        );
                        this.featureGroupCount = Object.keys(
                            this.featureGroups
                        ).length;
                    }
                });

                // load all the subsets.
                this.fontLoader
                    .get(this.targetStyle, true)
                    .then(() => {
                        // wait, then preload the remainder of the family for a better navigation experience.
                        return this.$timeout(() => {
                            if (!this.targetStyle) return;
                            this.familyService
                                .get(this.targetStyle.family)
                                .then((family) => {
                                    this.styleService
                                        .list({ id: family.style })
                                        .then(({ results: styles }) => {
                                            styles.forEach((style) =>
                                                this.fontLoader
                                                    .get(style)
                                                    .catch(() => {
                                                        console.warn(
                                                            'failed to load font file',
                                                            style
                                                        );
                                                    })
                                            );
                                        });
                                });
                        }, 20000);
                    })
                    .catch(() => {
                        console.warn(
                            'failed to load font file',
                            this.targetStyle
                        );
                    });
            }
        }
    }

    fetchStyleCharset(style: IFontStyleModel): IPromise<CharacterSet> {
        return this.djangoClient
            .get<{ character_set: CharacterSet }>(
                `/api/1/styles/${style.id}/charset`
            )
            .then((charsetResult) => {
                return charsetResult.character_set;
            });
    }

    _pad(n: unknown): string {
        return String('00000' + n).slice(-4);
    }

    getCharSetAlphaLanguages(charSet: CharacterSet): string[] {
        if (!hasOwnProperty(charSet, 'alphabets')) {
            return Object.keys(charSet);
        }
        let actual = Object.keys(charSet.alphabets);
        actual = actual.sort((a, b) => {
            if (a.toLowerCase() === 'latin') {
                return -1;
            }
            if (b.toLowerCase() === 'latin') {
                return 1;
            }
            return a.toLowerCase().localeCompare(b.toLowerCase());
        });
        return actual;
    }

    getFeatureGroups(
        style: IFontStyleModel,
        charSet: CharacterSet
    ): Record<string, { code: string; name: string }> {
        const featureGroups = {};
        if (!charSet || !style.support.languageSupport) {
            return featureGroups;
        }
        // var features = Object.keys(style.support.languageSupport);
        const features = style.support.otFeatures;
        featureGroupTitles.forEach((groupTitle) => {
            featureGroups[groupTitle] = [];
            OPENTYPE_FEATURE_GROUPS[groupTitle].forEach((feature) => {
                if (features.indexOf(feature) > -1) {
                    featureGroups[groupTitle].push({
                        code: feature,
                        name: OPENTYPE_FEATURES[feature],
                    });
                }
            });
        });

        return featureGroups;
    }

    setActiveLanguage(lang: string): void {
        this.activeLanguage = lang;
    }

    filterCodepageThreshold(
        support: CodePageCaseSupport
    ): Record<string, string> {
        const threshold = 0.9;
        const codepageSupport = {};
        Object.entries(support).forEach((entry) => {
            const [name, value] = entry;
            Object.entries(value).forEach((supportEntry) => {
                const [code, codeValue] = supportEntry;
                if (codeValue > threshold) {
                    codepageSupport[name]
                        ? codepageSupport[name].push(code)
                        : (codepageSupport[name] = [code]);
                }
            });
        });
        return codepageSupport;
    }

    getUnicodeCodepoint(glyph: string): string {
        let charcode = glyph.charCodeAt(0);
        // We need to validate if the glyph is a emoji to send the correct char code position
        // that matches to the submitted glyph
        if (isEmoji(glyph)) {
            // If glyph is Emoji, we need to decode from UTF-16 and get the code point
            // from the decoded UTF-32
            charcode =
                // We need to take the high surrogate subtract 0xD800, then multiply by 0x400
                (glyph.charCodeAt(0) - 0xd800) * 0x400 +
                (glyph.charCodeAt(1) - 0xdc00) +
                0x10000;
            // Then take the low surrogate and subtract 0xDC00, and finally add 0x10000 to get the final
            // decoded UTF-32 code point
            if (charcode < 0) {
                charcode = glyph.charCodeAt(0);
            }
        }
        return charcode.toString(16);
    }

    setActiveGlyph(glyph: string, otFeatureCode: string): IPromise<unknown> {
        const unicodeCodepoint = this.getUnicodeCodepoint(glyph);

        return this.djangoClient
            .get<UnicodeCasePair>(
                `/api/1/unicode/codepoint/${unicodeCodepoint}/`
            )
            .then((glyphData) => {
                this.activeGlyph = {
                    glyph: glyph,
                    unicode: this._pad(unicodeCodepoint),
                    html: '=#' + glyph.charCodeAt(0) + ';',
                    name: glyphData.name,
                    upper: glyphData.upper ? glyphData.upper.char : null,
                    lower: glyphData.lower ? glyphData.lower.char : null,
                };
                const otFeatures = opentypeFeatureCSS(otFeatureCode);
                this.glyphCss = `${this.fontCss}; ${otFeatures}`;
            });
    }

    makeGlyphActive(char: string): void {
        this.$timeout(() => angular.element(`#tn_glyph_${char}`).click(), 1);
    }

    ensureGlyphSpecimenPopover(): void {
        if (!this.glyphSpecimenPopover) {
            const element = angular.element('.popover-glyph-details');
            this.glyphSpecimenPopover = this.$tnPopover.popoverXS(element);
        }
    }

    onGlyphClicked($event: Event, glyph: string, otFeatureCode: string): void {
        $event.stopPropagation();
        this.ensureGlyphSpecimenPopover();
        this.setActiveGlyph(glyph, otFeatureCode);
        this.glyphSpecimenPopover?.show($event.currentTarget);
    }

    onExpandoClicked(): void {
        this.glyphSpecimenPopover?.hide();
    }
}

export const TnGlyphsComponentOptions = {
    bindings: {
        targetStyle: '<?',
        variationSettings: '<?',
    },
    controller: TnGlyphsComponent,
    template: tnGlyphsTemplate,
};
